import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { DamageTypeOption, MaterialType } from '../common/dropdowns';
import { getDropdownsOptions } from '../queries/appconfig/get-dropdowns';

const ROOT_DOMAIN_VALUE = 'ROOT';

export const useMaterialOptions = () => {
	const { data: dropdownsData } = useQuery(getDropdownsOptions());

	const materialsGroupByCategory = useMemo(
		() =>
			(dropdownsData?.materialOptions || [])?.reduce(
				(acc, mat) => {
					if (!mat.category) return acc;
					if (!acc[mat.category]) {
						acc[mat.category] = [];
					}
					acc[mat.category].push(mat);
					return acc;
				},
				{} as Record<string, MaterialType[]>,
			),
		[dropdownsData?.materialOptions],
	);

	// Create a map of material options by their ID for easy access later on
	const materialOptionsMap = useMemo(() => {
		const map: Record<number, MaterialType> = {};
		(dropdownsData?.materialOptions || []).forEach((mat) => {
			map[mat.id] = mat;
		});
		return map;
	}, [dropdownsData?.materialOptions]);

	return {
		materialOptions: dropdownsData?.materialOptions || [],
		materialMap: materialOptionsMap,
		materialsGroupByCategory: materialsGroupByCategory || {},
	};
};

export const useDamageTypeOptions = () => {
	const { data: dropdownsData } = useQuery(getDropdownsOptions());
	const { damageOptions, damageGroups } = dropdownsData || {};

	const damageOptionsByMaterialCategoryGroup = useMemo(() => {
		const damageGroupById = damageGroups?.reduce(
			(acc, group) => {
				acc[group.id] = group;
				return acc;
			},
			{} as Record<number, (typeof damageGroups)[number]>,
		);

		if (!damageOptions) return {};
		const damageOptionsObj = damageOptions.reduce(
			(acc, option) => {
				if (!option.materialCategory) return acc;
				if (!acc[option.materialCategory]) {
					acc[option.materialCategory] = {};
				}
				const group = damageGroupById?.[option.damageGroupId];
				if (!group) return acc;
				if (!acc[option.materialCategory][group.label]) {
					acc[option.materialCategory][group.label] = [];
				}
				acc[option.materialCategory][group.label].push(option);
				return acc;
			},
			{} as Record<string, Record<string, DamageTypeOption[]>>,
		);

		return Object.entries(damageOptionsObj).reduce(
			(acc, [materialCategory, groups]) => {
				acc[materialCategory] = Object.entries(groups).map(([groupLabel, options]) => ({
					name: groupLabel,
					options,
				}));
				return acc;
			},
			{} as Record<string, { name: string; options: DamageTypeOption[] }[]>,
		);
	}, [damageOptions, damageGroups]);

	const damageTypesMap = useMemo(() => {
		const map: Record<number, DamageTypeOption> = {};
		(damageOptions || []).forEach((option) => {
			map[option.id] = option;
		});
		return map;
	}, [damageOptions]);

	return {
		damageOptions: damageOptions || [],
		damageOptionsMap: damageTypesMap,
		damageOptionsByMaterialCategoryGroup,
	};
};

export const DROPDOWN_NAMES = {
	/**
	 * This dropdown is used for the damage statuses in the damage form.
	 */
	DAMAGE_STATUSES: 'DAMAGE_STATUSES',

	/**
	 * This dropdown is used for the damage categories in the damage form.
	 */
	DAMAGE_CATEGORIES: 'DAMAGE_CATEGORIES',

	/**
	 * This dropdown is used for the damage subcategories in the damage form.
	 */
	DAMAGE_SUBCATEGORIES: 'DAMAGE_SUBCATEGORIES',

	/**
	 * This dropdown is used for the damage remarks in the damage form.
	 */
	DAMAGE_REMARKS: 'DAMAGE_REMARKS',

	/**
	 * This dropdown is used for the damage extents in the damage form.
	 */
	DAMAGE_EXTENT: 'DAMAGE_EXTENT',

	/**
	 * This dropdown is used for the previous damage statuses in the damage form.
	 */
	PREVIOUS_DAMAGE_STATUSES: 'PREVIOUS_DAMAGE_STATUSES',

	/**
	 * This dropdown is used for the longitudinal positions in the damage form.
	 */
	LONGITUDINAL_POSITIONS: 'LONGITUDINAL_POSITIONS',

	/**
	 * This dropdown is used for the elevation positions in the damage form.
	 */
	ELEVATION_POSITIONS: 'ELEVATION_POSITIONS',

	/**
	 * This dropdown is used for the transverse positions in the damage form.
	 */
	TRANSVERSE_POSITIONS: 'TRANSVERSE_POSITIONS',
} as const;
export type DROPDOWN_NAMES = (typeof DROPDOWN_NAMES)[keyof typeof DROPDOWN_NAMES];

export const getDropdownOptionByDropdown = (dropdown: DROPDOWN_NAMES) => {
	const { data: domainValuesRes, isLoading } = useQuery(getDropdownsOptions());
	const domainValues = domainValuesRes?.domainValues || [];
	const rootDomain = domainValues.find((d) => d.value === ROOT_DOMAIN_VALUE);
	if (!rootDomain) {
		return { options: [], isGroup: false, isLoading };
	}
	const filtered = domainValues.filter((d) => d.group === dropdown);
	const isGroup = filtered.some((d) => d.parentId !== rootDomain.id && d.parentId !== null);
	return {
		options: filtered,
		isGroup,
		isLoading,
	};
};
